import { ApiRequest, ToastBuilder } from '../../../../../../../GlobalComponents/Builders';
import { handleRequiredFields, handleTextAreaObjectToString, convertObjectToString, parseJSONSafe } from '../../../../../../../GlobalComponents/Helpers';

const useDMTHandlers = ({
    jobData, jobResources, jobEventSource, availableResources, isAdmin,
    resetField, resourceResetField,
    setShowJobKeyMappings, setShowJobValueMappings, setShowAccordionResource,
    fetchResources, fetchSupplementaryData, fetchResourceMappings,
    fetchKeyMappings, fetchValueMappings, fetchDestinationResourceValues,
    getResourceKeyMappings, getResourceValueMappings, getJobMappings, getAllResourcesAndMappings,
}) => {

    const handleResourceUpdate = (update, identifier, connectionId) => {
        var updateArgs = {}
        updateArgs.connectionId = connectionId
        updateArgs.resourceId = identifier
        updateArgs.resourceName = update['name' + identifier]
        updateArgs.resourceOptions = handleTextAreaObjectToString(update['resourceOptions' + identifier]);
        updateArgs.resourceUsage = update['resourceUsage' + identifier] === 'default' ? null : (update['resourceUsage' + identifier] || null);

        if (update['isCustom' + identifier]) {
            updateArgs.isCustom = update['isCustom' + identifier];
            updateArgs.resource = update['resource' + identifier];
        }

        ApiRequest('PATCH', '/dmt/jobs/{jobId}/resources/{resourceId}', {
            pathParams: { jobId: jobData.id, resourceId: updateArgs['resourceId'] },
            body: updateArgs,
            toastOnSuccess: 'Resource updated',
        }).then(function () {
                setTimeout(() => {
                    fetchResources()
                    fetchSupplementaryData()
                }, 100)
            })
    }

    const handleResourceCreate = (formData, identifier, newResource = false, formHelpers = null) => {
        var updateArgs = {}
        let resourceOptions;
        let shouldRefreshSupplementary = false;

        if (newResource) {
            resourceOptions = handleTextAreaObjectToString(formData.newResourceOptions);
            const resourceUsage = formData.newResourceUsage || 'default';
            updateArgs = {
                serviceId: parseInt(identifier.service_id),
                connectionId: parseInt(identifier.id),
                isSetupResource: resourceUsage === 'setup' || resourceUsage === 'supplementary' ? 1 : 0,
                isSupplementaryResource: resourceUsage === 'supplementary' ? 1 : 0,
                resourceUsage: resourceUsage === 'default' ? null : resourceUsage,
                resource: formData.newResource,
                resourceName: formData.newResourceName,
                resourceOptions: resourceOptions
            }

            if (!handleRequiredFields(formData, ['newResourceName'])) return ToastBuilder('error', 'Resource Name is required.')
            if (identifier.service_code !== 'csv' && !updateArgs.isSetupResource && !updateArgs.resource) return ToastBuilder('error', 'Resource Endpoint is required.')

            shouldRefreshSupplementary = resourceUsage === 'supplementary' || resourceUsage === 'setup';
        }
        else {
            let selectedResource = availableResources[identifier] && availableResources[identifier].resources ? availableResources[identifier].resources.filter(resource => parseInt(resource.resource_id) === parseInt(formData['resource' + identifier])) : [];
            if (selectedResource.length > 0) {
                updateArgs.isSetupResource = selectedResource[0].is_setup_resource;
            }

            resourceOptions = handleTextAreaObjectToString(formData['resourceOptions' + identifier]);
            const resourceUsage = formData['resourceUsage' + identifier] || 'default';
            updateArgs = {
                connectionId: parseInt(identifier),
                resourceId: parseInt(formData['resource' + identifier]),
                isSetupResource: resourceUsage === 'setup' || resourceUsage === 'supplementary' ? 1 : (selectedResource.length > 0 ? selectedResource[0].is_setup_resource : 0),
                isSupplementaryResource: resourceUsage === 'supplementary' ? 1 : 0,
                resourceUsage: resourceUsage === 'default' ? null : resourceUsage,
                resourceOptions: resourceOptions
            }

            shouldRefreshSupplementary = resourceUsage === 'supplementary' || resourceUsage === 'setup';
        }

        const _resetField = formHelpers?.resetField || resourceResetField;

        ApiRequest('POST', '/dmt/jobs/{jobId}/resources', {
            pathParams: { jobId: jobData.id },
            body: updateArgs,
        }).then(function (results) {
                if(results?.data?.success) {
                    if (newResource) {
                        ToastBuilder('success', 'Resource Created Successfully')
                        _resetField('newResource')
                        _resetField('newResourceName')
                        _resetField('newResourceOptions')
                    } else {
                        ToastBuilder('success', 'Resource Added Successfully')
                        _resetField('resource' + identifier)
                        _resetField('resourceUsage' + identifier)
                        _resetField('resourceOptions' + identifier)
                    }
                }

                setTimeout(() => {
                    fetchResources(true)
                    fetchResourceMappings
                    shouldRefreshSupplementary ? fetchSupplementaryData() : null
                }, 300)
            })
    }

    const handleXMLDownload = (data, fileName) => {
        if (data.data) {
            const element = document.createElement("a");
            const file = new Blob([data.data], { type: 'xml' });
            element.href = URL.createObjectURL(file);
            element.download = fileName;
            document.body.appendChild(element);
            element.click();
        }
        else {
            ToastBuilder('error', 'We are unable to provide you with an export')
        }
    }

    const handleResourceDelete = (data, connectionId) => {
        const resource = jobResources[connectionId]?.selected_resources?.find(r => parseInt(r.resource_id) === parseInt(data.resource_id));
        const shouldRefreshSupp = resource && (resource.resource_usage === 'supplementary' || resource.resource_usage === 'setup');
        ApiRequest('DELETE', '/dmt/jobs/{jobId}/resources/{resourceId}', {
            pathParams: { jobId: jobData.id, resourceId: data.resource_id },
            body: { connectionId: connectionId },
        }).then(function () {
                setTimeout(() => {
                    fetchResources(false)
                    shouldRefreshSupp ? fetchSupplementaryData() : null
                }, 100)
            })
    }

    const handleJobAction = (action, sourceResourceId, myCallback = null, formData = null, showNotification = true) => {
        formData = formData ? Object.fromEntries(Object.entries(formData).filter(([key, _]) => !key.startsWith('sourceConnectionLog') && !key.startsWith('destinationConnectionLog'))) : null
        if (formData && formData.performUpdates) formData.performUpdates = formData.performUpdates === 'true';
        const refreshResources = !(['generate-data-preview'].includes(action))

        ApiRequest('POST', '/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId: jobData.id, actionName: action },
            body: { sourceResourceId, formData },
        }).then(function (results) {
                myCallback ? myCallback(results) : null
                showNotification ? ToastBuilder('success', 'Action Taken Successfully') : null
            })
    }

    const handleDMTAction = (action, sourceResourceId, myCallback = null, formData = null, showNotification = false) => {
        formData = formData ? Object.fromEntries(Object.entries(formData).filter(([key, _]) => !key.startsWith('sourceConnectionLog') && !key.startsWith('destinationConnectionLog'))) : null
        if (formData && formData.performUpdates) formData.performUpdates = formData.performUpdates === 'true';
        const refreshResources = !(['generate-data-preview'].includes(action))

        ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId: jobData.id, actionName: action },
            body: { sourceResourceId, formData },
        }).then(function (results) {
                myCallback ? myCallback(results) : null
                showNotification ? ToastBuilder('success', 'Action Taken Successfully') : null
            })
    }

    const handleResourceMappingCreate = (update) => {
        const sourceIsSetup = jobResources[jobData.source_connection.id].selected_resources.filter(resource => parseInt(resource.resource_id) === parseInt(update.sourceResourceToMap) && parseInt(resource.is_setup_resource) === 1).length > 0;
        const destinationIsSetup = jobResources[jobData.destination_connection.id].selected_resources.filter(resource => parseInt(resource.resource_id) === parseInt(update.destinationResourceToMap) && parseInt(resource.is_setup_resource) === 1).length > 0;

        var updateArgs = {
            notes: update.resourceNotes,
            sourceResourceId: update.sourceResourceToMap,
            destinationResourceId: update.destinationResourceToMap,
            afterCreateScripts: update.afterCreateScripts,
            mappingFilter: update.mappingFilter,
            mappingType: update.mappingType === 'default' ? null : (update.mappingType || null),
            mappingOptions: update.mappingType === 'composite' && update.chainResources ? { chain_resources: update.chainResources.map(r => parseInt(r)), chain_labels: update.chainLabels ? update.chainLabels.split(',').map(l => l.trim()) : [] } : null,
            sourceConnectionId: jobData.source_connection.id,
            destinationConnectionId: jobData.destination_connection.id,
            syncJobId: jobData.sync_job_id > 0 ? jobData.sync_job_id : null,
            isSetupMapping: sourceIsSetup && destinationIsSetup
        }

        if (update.defaultDependencies) {
            updateArgs.dependencies = update.defaultDependencies;
        }

        ApiRequest('POST', '/dmt/jobs/{jobId}/mappings', {
            pathParams: { jobId: jobData.id },
            body: updateArgs,
        }).then(function (results) {
                if (results?.data?.success) {
                    ToastBuilder('success', 'Resource Mapping Created Successfully')
                    resetField('sourceResourceToMap')
                    resetField('destinationResourceToMap')
                    resetField('defaultDependencies')
                    resetField('mappingFilter')
                    resetField('resourceNotes')
                    resetField('afterCreateScripts')
                    getJobMappings()
                } else {
                    ToastBuilder('error', 'Failed to create Resource Mapping')
                }
            })
    }

    const handleResourceMappingDelete = (update) => {
        const mappingId = `${update.source_resource_id}-${update.destination_resource_id}`
        ApiRequest('DELETE', '/dmt/jobs/{jobId}/mappings/{mappingId}', {
            pathParams: { jobId: jobData.id, mappingId },
        }).then(function () {
                fetchResourceMappings()
                getJobMappings()
            })
    }

    const handleKeyCreate = (update, identifier) => {
        ApiRequest('POST', '/dmt/jobs/{jobId}/resources/keys', {
            pathParams: { jobId: jobData.id },
            body: {
            key: update['keyName' + identifier],
            resourceId: identifier,
            connectionId: jobData.source_connection.id
        },
        })
            .then(function (results) {
                resetField('keyName' + identifier)
                setTimeout(() => fetchKeyMappings(), 15000)
            })
    }

    const handleSupplementaryValueCreate = (update, connectionId, resourceId, accordionIdentifier, bulk = false) => {
        const identifier = `${connectionId}-${resourceId}`;

        update = Object.entries(update).reduce((acc, [key, value]) => {
            if (key.includes(identifier)) {
                acc[key] = value;
            }
            return acc;
        }, {});

        var updateArgs = {
            resourceId: resourceId,
            connectionId: connectionId,
        }

        if (bulk) {
            updateArgs.valuesList = update['valuesList' + identifier]
            updateArgs.headersIncluded = update['headersIncluded' + identifier]
        }
        else {
            updateArgs.value = update['valueName' + identifier]
            updateArgs.valueId = update['valueId' + identifier]

            if(updateArgs.value === ''){
                return ToastBuilder('error', 'Value cannot be blank')
            }
        }

        ApiRequest('POST', '/dmt/jobs/{jobId}/resources/values', {
            pathParams: { jobId: jobData.id },
            body: updateArgs,
        }).then(function (results) {
            if (results.data && results.data.success){
                setShowAccordionResource(prev => ({
                    ...prev,
                    [accordionIdentifier]: false
                }));
                if (bulk){
                    resetField('valuesList' + identifier)
                    resetField('headersIncluded' + identifier)
                } else {
                    resetField('valueId' + identifier)
                    resetField('valueName' + identifier)
                }
                fetchSupplementaryData()
            } else {
                ToastBuilder('error', 'Something went wrong.')
            }
        })
    }

    const handleSupplementaryValueDelete = (row, connectionId, resourceId) => {
        ApiRequest('DELETE', '/dmt/jobs/{jobId}/resources/values/{valueId}', {
            pathParams: { jobId: jobData.id, valueId: row.resource_value_id },
            body: { connectionId, resourceId },
        }).then(function (results) {
                if(results?.data?.success){
                    ToastBuilder('success', 'Value Deleted Successfully')
                    setTimeout(() => fetchSupplementaryData(), 100)
                } else {
                    ToastBuilder('error', 'Something went wrong.')
                }
            })
    }

    const handleValueCreate = (update, identifier, accordionIdentifier, connectionData, bulk = false) => {
        update = Object.entries(update).reduce((acc, [key, value]) => {
            if (key.includes(identifier)) {
                acc[key] = value;
            }
            return acc;
        }, {});

        var updateArgs = {
            resourceId: identifier,
            connectionId: connectionData.id,
        }

        if (bulk) {
            updateArgs.valuesList = update['valuesList' + identifier]
            updateArgs.headersIncluded = update['headersIncluded' + identifier]
        }
        else {
            updateArgs.value = update['valueName' + identifier]
            updateArgs.valueId = update['valueId' + identifier]

            if(updateArgs.value === ''){
                return ToastBuilder('error', 'Value cannot be blank')
            }
        }

        ApiRequest('POST', '/dmt/jobs/{jobId}/resources/values', {
            pathParams: { jobId: jobData.id },
            body: updateArgs,
        }).then(function (results) {
            if (results.data && results.data.success){
                setShowAccordionResource(prev => ({
                    ...prev,
                    [accordionIdentifier]: false
                }));

                resetField('valuesList' + identifier)
                resetField('valueName' + identifier)
                resetField('valueId' + identifier)
                resetField('headersIncluded' + identifier)
                setTimeout(() => fetchValueMappings(identifier), 5000)
            } else {
                ToastBuilder('error', 'Something went wrong.')
            }
        })
    }

    const handleDestinationValueCreate = (update, identifier, accordionIdentifier, connectionData, bulk = false) => {
        update = Object.entries(update).reduce((acc, [key, value]) => {
            if (key.includes(identifier)) {
                acc[key] = value;
            }
            return acc;
        }, {});

        var updateArgs = {
            resourceId: identifier,
            connectionId: connectionData.id,
        }

        if (bulk) {
            updateArgs.valuesList = update['valuesList' + identifier]
            updateArgs.headersIncluded = update['headersIncluded' + identifier]
        }
        else {
            updateArgs.value = update['valueName' + identifier]
            updateArgs.valueId = update['valueId' + identifier]

            if(updateArgs.value === ''){
                return ToastBuilder('error', 'Value cannot be blank')
            }
        }

        ApiRequest('POST', '/dmt/jobs/{jobId}/resources/values', {
            pathParams: { jobId: jobData.sync_job_id > 0 ? jobData.sync_job_id : jobData.id },
            body: updateArgs,
        }).then(function (results) {
            if (results.data && results.data.success){
                setShowAccordionResource(prev => ({
                    ...prev,
                    [accordionIdentifier]: false
                }));

                resetField('valuesList' + identifier)
                resetField('valueName' + identifier)
                resetField('valueId' + identifier)
                resetField('headersIncluded' + identifier)
                setTimeout(() => {
                    fetchDestinationResourceValues([], accordionIdentifier)
                }, 1000)
            } else {
                ToastBuilder('error', 'Something went wrong.')
            }
        })
    }

    const handleMappingUpdate = (update, identifier, mappingType, rowData = null) => {
        var serviceId = jobData && jobData.source_connection ? jobData.source_connection.service_id : null
        if (!serviceId) return false

        var updateArgs = {}

        const hasMatchingKeys = Object.keys(update).some(key => key.endsWith(identifier));

        if (mappingType === 'resource' && hasMatchingKeys) {
            updateArgs.jobId = jobData.id
            updateArgs.sourceResourceId = rowData.source_resource_id
            updateArgs.destinationResourceId = rowData.destination_resource_id
            updateArgs.notes = update['notes' + identifier]

            let mappingFilterObj = parseJSONSafe(update['mappingFilter' + identifier])

            if (typeof mappingFilterObj === 'string') {
                let hasSpecialWhiteSpace = mappingFilterObj.includes('\u2003');
                console.warn('Checking for special characters', hasSpecialWhiteSpace)
                mappingFilterObj = mappingFilterObj.replaceAll(/\u2003/g, ' ');
            }

            if (typeof mappingFilterObj !== 'object' && mappingFilterObj !== null && typeof mappingFilterObj === 'string' && ( mappingFilterObj.includes('&') || mappingFilterObj.includes('^') ) ){
                let replaceKey = jobEventSource !== 'servicenow' ? '&' : '^';
                let replaceRegex = jobEventSource !== 'servicenow' ? /\s*&\s*/g : /\s*\^\s*/g;
                mappingFilterObj = mappingFilterObj.replace(replaceRegex, replaceKey);
                updateArgs.mappingFilter = mappingFilterObj.replace(/\u2003/g, ' ');
            } else {
                updateArgs.mappingFilter = convertObjectToString(mappingFilterObj)
            }

            updateArgs.afterCreateScripts = update['afterCreateScripts' + identifier]
            let afterCreateScripts = update['afterCreateScripts' + identifier]

            if (afterCreateScripts && typeof afterCreateScripts === 'string'){
                updateArgs.afterCreateScripts = afterCreateScripts
                    .replace(/\s*\n+\s*/g, ',')
                    .replace(',,', ',')
                    .replace(/^,+|,+$/g, '')
                    ;
            }

            updateArgs.mappingType = update['mappingType' + identifier] === 'default' ? null : (update['mappingType' + identifier] || null);

            // Mapping options from raw JSON textarea
            const rawMappingOptions = update['mappingOptions' + identifier];
            if (rawMappingOptions && rawMappingOptions.trim()) {
                try {
                    const parsed = JSON.parse(rawMappingOptions);
                    if (parsed && typeof parsed === 'object') {
                        updateArgs.mappingOptions = parsed;
                    }
                } catch (e) {
                    ToastBuilder('error', 'Mapping Options contains invalid JSON');
                    return;
                }
            } else {
                updateArgs.mappingOptions = null;
            }

            const dependenciesFieldName = 'defaultDependencies' + identifier;
            if (update[dependenciesFieldName]) {
                updateArgs.dependencies = update[dependenciesFieldName];
            }

            ApiRequest('PATCH', '/dmt/jobs/{jobId}/mappings/{mappingId}', {
                pathParams: { jobId: jobData.id, mappingId: `${rowData.source_resource_id}-${rowData.destination_resource_id}` },
                body: updateArgs,
            }).then(function (results) {
                    if(results.data.reload) {
                        const loadingTime = 15000
                        results.data.success ? ToastBuilder('success', 'Loading new Resources...', {}, loadingTime) : null
                        setTimeout(() => getAllResourcesAndMappings(), loadingTime)
                    }
                    else {
                        results.data.success ? ToastBuilder('success', 'Resource Mapping Updated', {}, 750) : null
                        getJobMappings()
                    }
                })
        }
        else if (mappingType === 'key' && hasMatchingKeys ) {
            updateArgs.jobId = jobData.id
            updateArgs.sourceKey = rowData.source_resource_key_id
            updateArgs.destinationKey = update['destinationResourceKeyId' + identifier]
            updateArgs.notes = update['notes' + identifier]
            updateArgs.status = update['status' + identifier]
            updateArgs.mappingCode = update['code' + identifier] ? update['code' + identifier] : ''
            updateArgs.defaultValue = update['defaultValue' + identifier]
            updateArgs.onFailure = update['onFailure' + identifier]
            updateArgs.takeEffectOn = update['takeEffectOn' + identifier]
            updateArgs.order = update['order' + identifier]

            updateArgs.mappingConditions = handleTextAreaObjectToString(update['conditions' + identifier])
            updateArgs.persistentDataKey = update['persistentDataKey' + identifier] ? parseInt(update['persistentDataKey' + identifier]) : 0
            updateArgs.parentKeyId = update['parentKeyId' + identifier] || null

            // Include renamed source key if changed (custom keys only)
            const newSourceKeyName = update['sourceKey' + identifier]
            if (newSourceKeyName && newSourceKeyName !== rowData.source_key) {
                updateArgs.sourceKeyName = newSourceKeyName
                updateArgs.sourceResourceId = rowData.source_resource_id
            }

            ApiRequest('PATCH', '/dmt/jobs/{jobId}/keymappings/{resourceKeyId}', {
                pathParams: { jobId: jobData.id, resourceKeyId: rowData.source_resource_key_id },
                body: updateArgs,
                toastOnSuccess: 'Resource Key Mapping Updated',
                toastOnError: true,
            }).then(function () {
                    fetchKeyMappings(rowData.source_resource_id)
             })
        }
        else if (mappingType === 'value') {
            Object.keys(update).forEach(item => {
                if (item.includes(identifier)) {
                    updateArgs.jobId = jobData.id
                    updateArgs.notes = update['notes' + identifier]
                    updateArgs.status = update['status' + identifier]
                    updateArgs.sourceValueId = rowData.source_resource_value_id
                    updateArgs.destinationValueIdNew = update['destinationResourceValueId' + identifier]
                    updateArgs.destinationValueIdOld = update['actualDestinationResourceValueId' + identifier]
                    updateArgs.ignoreDestinationParent = update['ignoreDestinationParent' + identifier] ? 1 : 0
                }
            })

            if (isAdmin) updateArgs.sourceValue = update['sourceValue' + identifier]

            ApiRequest('PATCH', '/dmt/jobs/{jobId}/valuemappings/{resourceValueId}', {
                pathParams: { jobId: jobData.id, resourceValueId: rowData.source_resource_value_id },
                body: updateArgs,
            }).then(function (results) {
                    fetchValueMappings(rowData.source_resource_id)
                })
        }
        else {
            ToastBuilder('error', 'Invalid Mapping Type')
        }
    }

    return {
        handleResourceUpdate, handleResourceCreate, handleXMLDownload, handleResourceDelete,
        handleJobAction, handleDMTAction,
        handleResourceMappingCreate, handleResourceMappingDelete,
        handleKeyCreate, handleSupplementaryValueCreate, handleSupplementaryValueDelete,
        handleValueCreate, handleDestinationValueCreate, handleMappingUpdate,
    }
}

export default useDMTHandlers;
